/**
 * "Is this **account** a user of **that** hub?" — the evidence the discovery
 * list reads before it offers a connection instead of an enrollment
 * (`config/onboarding.ts` → `resolveDiscoveredHubPlan`).
 *
 * ── The question this module no longer asks ─────────────────────────────────
 *
 * It used to answer "does this device hold a credential **for this address
 * spelling**", by walking credential keys (`hub:<hubId>`,
 * `local:<host>:<port>/api`, the host-only legacy spelling) and the
 * saved-connection list. That substitution is the measured defect of
 * 2026-09-24 (plan `docs/plans/LOCAL-FIRST-CONNECT.md` §2/D4, evidence §3): the
 * one credential on the device lived under the host-only loopback key
 * `torik_device_token:local:127.0.0.1/api` (`role: owner`), the walk skips
 * exactly that spelling once the hub names itself, so a hub the same device held
 * an owner credential for read as "nothing here" — and the launcher offered to
 * adopt a hub its own account owned. `hubBindingKeys()` is gone with it.
 *
 * ── What it answers now ─────────────────────────────────────────────────────
 *
 * One fact this app can actually establish (plan §2/D4): the **account's own hub
 * list** (`services/accountHubs.ts`, `GET /hubs`) holds a row for the hub that
 * answered at this address. The hub's identity at that address is the second
 * half: its own `hubId` (`GET /api/hub/identity`, or mDNS TXT) and/or the
 * `publicId` the responder named. A hub is bound when
 *
 *   1. the identity it named is a row of this account — the row's own id
 *      (`macAddress`) or the cloud row id (`id`), the same pair the launcher
 *      merges its two card lists on; or
 *   2. the account's `hubPublicId` for a row equals the `publicId` the responder
 *      named (the rule `services/hubLocalFirst.ts` claims with, applied to the
 *      card).
 *
 * ── What it deliberately does not answer ────────────────────────────────────
 *
 * The **device-credential class** — a wall panel, an installer's device — has no
 * account and cannot claim, so its installer grant and binding stay exactly as
 * they are (§2/D4) and its reader stays (`services/deviceCredential.ts`). The
 * surfaces ask about that class separately (`hasStoredDeviceCredential`, carried
 * as the table's own `hasDeviceCredential` fact): a hub this account does not
 * know but this device holds a credential for is still a hub it can OPEN.
 *
 * Nothing here enrolls, redeems, claims or connects: the module answers one
 * question about two facts, and the decision it feeds is a pure function.
 */
import { buildHubApiUrl, getHubOwnKeys } from '../config/runtime';
import { isHostScopedCredentialKey, readDeviceCredential } from './deviceCredential';
import type { AccountHubRow } from './accountHubs';

export interface HubBindingTarget {
  ip: string;
  port: number;
  /**
   * The scheme of the address, when it is known (`true` = TLS). Kept because
   * every caller holds it and the device-credential evidence below is
   * scheme-aware (`services/deviceCredential.ts` keys by endpoint).
   */
  secure?: boolean;
  /**
   * The hub's own id, when something already told us: discovery's mDNS TXT /
   * `/api/info` `hubId`, or `GET /api/hub/identity` (F11) — the id the account's
   * hub list records as `macAddress`.
   */
  hubId?: string | null;
  /**
   * The `publicId` the responder at this address named (`GET /api/hub/identity`),
   * when it answered. Compared with the account's own `hubPublicId` for a row:
   * the same comparison the LAN claim is gated on, so a card and the route it
   * opens cannot disagree.
   */
  publicId?: string | null;
}

function normalizeId(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim().toLowerCase();
  return trimmed.length ? trimmed : null;
}

/**
 * The account's row for this hub, or null when the account does not hold it.
 *
 * Pure, so the rule is testable without a browser: the ids are compared
 * case-insensitively (a UUID's case is not a fact about a hub), and a target
 * that knows neither id nor publicId matches nothing — "I could not read who
 * you are" must never answer "yes, the account holds it".
 */
export function accountHubFor(target: HubBindingTarget, accountHubs: AccountHubRow[] = []): AccountHubRow | null {
  if (!Array.isArray(accountHubs) || !accountHubs.length) return null;
  const hubId = normalizeId(target.hubId);
  const publicId = normalizeId(target.publicId);

  for (const row of accountHubs) {
    if (!row) continue;
    if (hubId && (normalizeId(row.hubId) === hubId || normalizeId(row.id) === hubId)) return row;
    if (publicId && normalizeId(row.publicId) === publicId) return row;
  }
  return null;
}

/**
 * The whole question: is this hub one of the account's own?
 *
 * Synchronous on purpose: the discovery list asks it once per card while it
 * renders, and the asynchronous half (the account's hub list) is a cache the
 * caller has already warmed (`services/accountHubs.ts` → `loadAccountHubs`, or
 * the launcher's own `GET /hubs`).
 *
 * Best effort by design: an empty list (no cloud session, a failed load)
 * answers "not bound", which offers the way in that does not need an account —
 * join, adopt, or the device credential of a panel.
 */
export function isHubBound(target: HubBindingTarget, accountHubs: AccountHubRow[] = []): boolean {
  return accountHubFor(target, accountHubs) !== null;
}

export interface HubBindingOptions {
  /** Injected by tests; the runtime reads `localStorage`. */
  storage?: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> | null;
}

/**
 * Does this device hold the hub's own **device credential** for this address
 * (F5)? The panel/installer class's door — a wall panel has no account and
 * cannot claim, so this is what opens its hub.
 *
 * Read through `readDeviceCredential`, which is the module that owns the storage
 * policy (which key shapes exist, and what a valid `typ:"device"` credential
 * is). Since card `t_6cbbce4c` no user-device flow writes one any more (the
 * claim's hub session is what a user's own device keeps), so an entry here is a
 * panel, an installer's device, or a credential an older build left behind.
 */
export function hasStoredDeviceCredential(target: HubBindingTarget, options: HubBindingOptions = {}): boolean {
  const keys = deviceCredentialKeys(target);
  if (!keys.length) return false;
  try {
    return readDeviceCredential({ keys, storage: options.storage ?? undefined }) !== null;
  } catch {
    return false;
  }
}

/**
 * Every key a credential for this hub could live under, most specific first.
 *
 * Kept here (the panel question is still asked) but no longer a *binding*
 * approximation: the two spellings are the hub's own id (`hub:<hubId>`) and the
 * endpoint of the address being offered at — the shapes the enrollment flow
 * writes today (`services/deviceCredential.ts`).
 *
 * The **host-only** spelling (`local:<host>/api`, the pre-port legacy key) is
 * included only for a hub that cannot name itself — see
 * `isHostScopedCredentialKey`, which owns that rule and the reasoning: a key
 * shared by every hub on a host must not answer for one of them.
 */
export function deviceCredentialKeys(target: HubBindingTarget): string[] {
  const keys: string[] = [];
  const hubId = typeof target.hubId === 'string' ? target.hubId.trim() : '';
  if (hubId) keys.push(`hub:${hubId}`);
  try {
    // The endpoint keys of the address, in the scheme it was reached on (card
    // `t_cf6ea0e5`), from the one module that owns the key shapes
    // (`config/runtime.ts` → `localHubSessionKeys`): a hub reached over https
    // and http is two connections that must not share a credential.
    for (const key of getHubOwnKeys({ apiUrl: buildHubApiUrl(target.ip, target.port, target.secure) })) {
      if (hubId && isHostScopedCredentialKey(key)) continue;
      if (!keys.includes(key)) keys.push(key);
    }
  } catch {
    // No runtime config yet (a test, a very early boot): the hub id key, when
    // there is one, is still a valid place to look.
  }
  return keys;
}
